/**
 * A background engine of the prospect funnel, drawn as one strip: what it is,
 * whether it is on, when it last ran and runs next, and the three things an
 * operator does with it on an ordinary day - run it now, switch it off, look
 * at its settings.
 *
 * WHY A STRIP AND NOT THE CARD IT WAS. The scan autopilot, the brain autopilot
 * and the lead watch each used to be a full card: a head, four or five number
 * tiles, a last-run sentence, and the settings form and run log unfolding
 * under a chevron. Each sits on the tab whose rows it produces (Skannid,
 * Arendusaju, Rikked), and as cards they were the first thing on those tabs -
 * a wall of configuration between the tab strip and the list the tab is for.
 * What an operator needs every day fits on one line; the numbers, the form and
 * the log are what they open now and then, so those went behind the gear.
 *
 * WHY A SHEET FOR THE SETTINGS. A side panel keeps the list in view behind it,
 * so changing the batch size does not scroll the rows it affects off screen,
 * and closing it puts focus back on the gear. It is the kit's `Sheet` (Radix
 * dialog underneath: Escape, focus trap, scroll lock) rather than the chevron
 * disclosure the cards had, which pushed the list down by a form's height.
 *
 * THE SWITCH WRITES STRAIGHT THROUGH. A stop button with a save step is not a
 * stop button, and this strip is where an engine gets stopped. It and "run
 * now" are drawn only for `canManage` - the same key the server checks for
 * every write here - while the gear is for everyone: reading how an engine is
 * set up is not a change.
 *
 * Everything engine-specific stays with the engine's own file, which hands
 * this strip its words and its sheet body: `scan-leads/AutopilotPanel.tsx`,
 * `brain/BrainAutopilotPanel.tsx` and `scan-leads/watch/WatchPanel.tsx`.
 */
import { useRef, useState, type ReactNode } from "react";
import { Loader2, Play, Settings2, type LucideIcon } from "lucide-react";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Switch,
  cn,
} from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { Btn, IconBtn } from "../../../ui";

export interface EngineRun {
  /** The button's wording, already chosen for the state (run now / running). */
  label: string;
  /** A batch is running or the button's own request is in flight. */
  pending: boolean;
  onRun: () => void;
}

export function EngineLine({
  icon: Icon,
  title,
  status,
  when,
  enabled,
  busy,
  canManage,
  saving,
  onToggle,
  run,
  actions,
  note,
  settingsLabel,
  children,
  className,
}: {
  icon: LucideIcon;
  title: string;
  /** One short sentence: on and how often, off, or working right now. */
  status: string;
  /** The last run and the next one, as one line. */
  when?: ReactNode;
  enabled: boolean;
  busy?: boolean;
  canManage: boolean;
  /** A save of the switch is in flight. */
  saving?: boolean;
  /** Left out, there is no switch - an engine whose state failed to load. */
  onToggle?: (on: boolean) => void;
  run?: EngineRun;
  /** Controls of this engine's own, drawn before the switch. */
  actions?: ReactNode;
  /** A paragraph under the line, inside the strip: what an engine that is off
   *  would do, or the answer to the last thing the operator asked of it. */
  note?: ReactNode;
  settingsLabel: string;
  /** What the gear opens: the numbers, the settings form and the run log. */
  children?: ReactNode;
  className?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * THE PANEL COMMITS WHAT WAS TYPED BEFORE IT CLOSES. The number fields in it
   * save on blur (`EngineNumberField`, the lead watch's own field), and Escape
   * closes the panel by taking it out of the page - a field removed while it
   * has focus fires no blur, so a batch size typed and not yet confirmed was
   * dropped without a word while the panel closed as if it had been kept. A
   * click on the cross or outside the panel blurs the field on the way, which
   * is why only Escape lost the edit. Blurring the focused field first runs
   * that same commit, whichever way the panel is closed.
   */
  const onOpenChange = (next: boolean) => {
    if (!next) {
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && panelRef.current?.contains(focused)) focused.blur();
    }
    setOpen(next);
  };

  // The whole strip sits inside the sheet's root (which draws nothing) so the
  // gear can be its `SheetTrigger`: that is what gives focus back to the gear
  // when the panel closes, and what wires `aria-expanded` and `aria-controls`.
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <section className={cn("crm-card mb-4", className)} aria-label={title}>
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 px-5 py-3.5">
          {/* The tint is an inline style, the way `Kpi` does it: one
              `color-mix` paints both the tile and the glyph, and a content scan
              that never saw an arbitrary class written out cannot drop it. */}
          <span
            className="crm-kpi-tile size-9 rounded-xl"
            style={
              enabled
                ? {
                    background: "color-mix(in oklab, var(--color-primary) 13%, transparent)",
                    color: "var(--color-primary)",
                  }
                : { background: "var(--crm-sunk)", color: "var(--color-muted-foreground)" }
            }
          >
            <Icon className={cn("size-4", busy && "animate-pulse")} strokeWidth={1.8} />
          </span>

          {/* Basis 15rem and allowed to grow: beside the controls on a desk,
              and on a phone the controls wrap under it instead of squeezing the
              sentence to one word a line. One `flex` value rather than `flex-1`
              plus `basis-60`: both write `flex-basis`, and `flex-1` wins. */}
          <div className="min-w-0 flex-[1_1_15rem]">
            <p className="text-[13.5px] leading-snug">
              <span className="font-bold tracking-[-0.01em]">{title}</span>{" "}
              <span className="crm-sub">{status}</span>
            </p>
            {when ? <p className="crm-mut text-[12px] leading-snug">{when}</p> : null}
          </div>

          <div className="flex flex-none flex-wrap items-center gap-2">
            {canManage && run ? (
              <Btn small disabled={run.pending} onClick={run.onRun}>
                {run.pending ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
                {run.label}
              </Btn>
            ) : null}
            {actions}
            {canManage && onToggle ? (
              <Switch
                checked={enabled}
                disabled={saving}
                onCheckedChange={onToggle}
                aria-label={title}
                className="mx-1"
              />
            ) : null}
            {children ? (
              <SheetTrigger asChild>
                <IconBtn small className={cn(open && "is-on")} aria-label={settingsLabel} title={settingsLabel}>
                  <Settings2 className="size-4" />
                </IconBtn>
              </SheetTrigger>
            ) : null}
          </div>
        </div>

        {note ? <div className="border-t border-[var(--crm-hair)] px-5 py-3">{note}</div> : null}
      </section>

      {children ? (
        // Wider than the kit's default: the run logs inside are lists with a
        // floor of about 900px (`listFloor`), and below it they scroll sideways
        // inside the panel. On a phone the panel is the screen.
        //
        // Focus lands on the panel, not on its first field: Radix would put it
        // in the interval box with the number selected, one stray key away from
        // a new schedule for the engine.
        <SheetContent
          ref={panelRef}
          className="sm:max-w-[960px]"
          closeLabel={t("common.close")}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).focus();
          }}
        >
          <SheetHeader className="px-[26px] pr-14">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{status}</SheetDescription>
          </SheetHeader>
          {/* No side padding of its own: the numbers, the form and the log
              were drawn for a card and keep the card's 26px gutter. */}
          <SheetBody className="px-0 pb-6 pt-1">{children}</SheetBody>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}
